import { isResting, type Band, type Card, type Motivation } from '@hmawiw/shared';
import { boardCards, cellCards } from '../store/cards';
import { DAY_MS, hasDueTime, startOfDay, withTime } from './dates';

/** Local Monday-to-Sunday weeks; the end is the last millisecond of Sunday. */
export function endOfWeek(now: number): number {
  const d = new Date(startOfDay(now));
  const daysToSunday = (7 - d.getDay()) % 7;
  d.setDate(d.getDate() + daysToSunday);
  return d.getTime() + DAY_MS - 1;
}

/**
 * Horizon mode reads the band off the due date: overdue or due today is Today, due by Sunday
 * is This week, anything later or undated is Whenever. It changes as the days pass.
 */
export function horizonBand(card: Pick<Card, 'dueAt'>, now: number): Band {
  if (card.dueAt === null) return 2;
  if (startOfDay(card.dueAt) <= startOfDay(now)) return 0;
  return card.dueAt <= endOfWeek(now) ? 1 : 2;
}

/**
 * The due date a drop into a horizon band means: Today sets today (a time of day survives),
 * This week keeps a date already inside the week and otherwise sets Sunday, Whenever clears.
 */
export function dueForHorizon(band: Band, card: Pick<Card, 'dueAt'>, now: number): number | null {
  const time = card.dueAt !== null && hasDueTime(card.dueAt) ? card.dueAt : null;
  const keepTime = (day: number) => {
    if (time === null) return day;
    const t = new Date(time);
    return withTime(day, `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`);
  };
  if (band === 0) return keepTime(startOfDay(now));
  if (band === 2) return null;
  if (card.dueAt !== null && horizonBand(card, now) === 1) return card.dueAt;
  return keepTime(startOfDay(endOfWeek(now)));
}

function byRank(a: Card, b: Card): number {
  return a.rank - b.rank || a.id.localeCompare(b.id);
}

/**
 * The cards of one cell under the active preset. Importance is the stored band in manual
 * order; Horizon groups by due date, soonest first in the dated bands and by hand in Whenever.
 */
export function cellCardsFor(cards: Record<string, Card>, motivation: Motivation, band: Band, preset: 'importance' | 'horizon', now: number): Card[] {
  if (preset === 'importance') return cellCards(cards, motivation, band).filter((c) => !isResting(c, now));
  const list = boardCards(cards, now).filter((c) => c.motivation === motivation && horizonBand(c, now) === band);
  if (band === 2) return list.sort(byRank);
  return list.sort((a, b) => (a.dueAt as number) - (b.dueAt as number) || byRank(a, b));
}
